import React, { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  ArrowLeft,
  Save,
  Loader2,
  Shield,
  FileText,
  MessageSquare,
  Lock,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import type { QARun, QARunResults, QATestResult, computeQASummary } from '@/types/qaTypes';
import { useAdminAuth } from '@/hooks/useAdminAuth';

interface QARunDetailProps {
  run: QARun;
  passCriteria: { min_pass_rate: number; hard_fail_categories: string[] };
  onBack: () => void;
  onUpdate: () => void;
}

const categoryIcons: Record<string, React.ReactNode> = {
  refusal: <Shield className="w-4 h-4" />,
  citation: <FileText className="w-4 h-4" />,
  escalation: <MessageSquare className="w-4 h-4" />,
  pii: <Lock className="w-4 h-4" />,
};

const QARunDetail: React.FC<QARunDetailProps> = ({
  run,
  passCriteria,
  onBack,
  onUpdate,
}) => {
  const { user } = useAdminAuth();
  const [results, setResults] = useState<QARunResults | null>(run.results);
  const [saving, setSaving] = useState(false);

  const handleOverride = (testIndex: number, finalPass: boolean | null) => {
    if (!results) return;
    
    const updated = { ...results };
    updated.test_results[testIndex] = {
      ...updated.test_results[testIndex],
      operator_review: {
        ...updated.test_results[testIndex].operator_review,
        final_pass: finalPass,
        reviewed_at: finalPass !== null ? new Date().toISOString() : null,
        reviewed_by: finalPass !== null ? user?.id || null : null,
      },
    };
    setResults(updated);
  };

  const handleNotesChange = (testIndex: number, notes: string) => {
    if (!results) return;
    
    const updated = { ...results };
    updated.test_results[testIndex] = {
      ...updated.test_results[testIndex],
      operator_review: {
        ...updated.test_results[testIndex].operator_review,
        notes,
      },
    };
    setResults(updated);
  };

  const saveReviews = async () => {
    if (!results) return;
    
    setSaving(true);
    
    try {
      // Recompute summary based on reviews
      const totalTests = results.test_results.length;
      const passedResults = results.test_results.filter(r => {
        const finalResult = r.operator_review.final_pass !== null 
          ? r.operator_review.final_pass 
          : r.auto_eval.passed;
        return finalResult === true;
      });
      
      const failedResults = results.test_results.filter(r => {
        const finalResult = r.operator_review.final_pass !== null 
          ? r.operator_review.final_pass 
          : r.auto_eval.passed;
        return finalResult === false;
      });
      
      const passRate = totalTests > 0 ? passedResults.length / totalTests : 0;
      const hardFailCount = failedResults.filter(r => 
        passCriteria.hard_fail_categories.includes(r.category)
      ).length;
      const overallPass = passRate >= passCriteria.min_pass_rate && hardFailCount === 0;
      
      const updatedResults: QARunResults = {
        ...results,
        summary: {
          total_tests: totalTests,
          passed_auto: passedResults.length,
          failed_auto: failedResults.length,
          pass_rate: Math.round(passRate * 100) / 100,
          hard_fail_count: hardFailCount,
          overall_pass: overallPass,
        },
      };
      
      const { error } = await supabase
        .from('qa_runs')
        .update({
          results: updatedResults as any,
          passed: overallPass,
        })
        .eq('id', run.id);
      
      if (error) throw error;
      
      toast({ title: 'Reviews saved' });
      onUpdate();
    } catch (error) {
      console.error('Error saving reviews:', error);
      toast({
        title: 'Error saving reviews',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!results) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          No results data available for this run.
        </CardContent>
      </Card>
    );
  }

  const summary = results.summary;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Run History
        </Button>
        <Button onClick={saveReviews} disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Reviews
            </>
          )}
        </Button>
      </div>

      {/* Summary Card */}
      <Card className={summary.overall_pass ? 'border-green-500/50' : 'border-destructive/50'}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {summary.overall_pass ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <XCircle className="w-5 h-5 text-destructive" />
            )}
            Run Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Total Tests</p>
              <p className="text-2xl font-bold">{summary.total_tests}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Pass Rate</p>
              <p className="text-2xl font-bold">{(summary.pass_rate * 100).toFixed(0)}%</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Passed</p>
              <p className="text-2xl font-bold text-green-600">{summary.passed_auto}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Hard Fails</p>
              <p className="text-2xl font-bold text-destructive">{summary.hard_fail_count}</p>
            </div>
          </div>
          
          <div className="mt-4 flex gap-2">
            <Badge variant="outline">
              Suite: {results.suite_id} v{results.suite_version}
            </Badge>
            <Badge variant="outline">
              <Clock className="w-3 h-3 mr-1" />
              {new Date(run.started_at || run.created_at).toLocaleString()}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Test Results */}
      <div className="space-y-4">
        <h3 className="font-semibold">Test Results</h3>
        {results.test_results.map((test, index) => {
          const autoResult = test.auto_eval.passed;
          const operatorResult = test.operator_review.final_pass;
          const finalResult = operatorResult !== null ? operatorResult : autoResult;
          
          return (
            <Card 
              key={test.test_id}
              className={`${
                finalResult === true
                  ? 'bg-green-500/5 border-green-500/20'
                  : finalResult === false
                  ? 'bg-destructive/5 border-destructive/20'
                  : 'bg-amber-500/5 border-amber-500/20'
              }`}
            >
              <CardContent className="p-4 space-y-4">
                {/* Test Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {finalResult === true ? (
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    ) : finalResult === false ? (
                      <XCircle className="w-5 h-5 text-destructive mt-0.5" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                    )}
                    <div>
                      <p className="font-medium">{test.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {test.test_id}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {categoryIcons[test.category]}
                    <Badge variant="secondary">{test.category}</Badge>
                  </div>
                </div>

                {/* Response */}
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Bot Response:</p>
                  <div className="bg-muted p-3 rounded text-sm">
                    {test.auto_eval.response_text || 'No response captured'}
                  </div>
                </div>

                {/* Auto Eval Details */}
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span>
                    Auto: {autoResult === true ? '✓ Pass' : autoResult === false ? '✕ Fail' : '? Inconclusive'}
                  </span>
                  <span>Checks: {test.auto_eval.checks_run.join(', ')}</span>
                  {test.auto_eval.escalated && (
                    <Badge variant="outline" className="text-xs">Escalated</Badge>
                  )}
                </div>

                {/* Operator Review */}
                <div className="border-t pt-4 space-y-3">
                  <p className="text-sm font-medium">Operator Review</p>
                  
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <Switch
                        id={`pass-${test.test_id}`}
                        checked={test.operator_review.final_pass === true}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            handleOverride(index, true);
                          } else if (test.operator_review.final_pass === true) {
                            handleOverride(index, null);
                          }
                        }}
                      />
                      <Label htmlFor={`pass-${test.test_id}`} className="text-sm">
                        Mark as Pass
                      </Label>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        id={`fail-${test.test_id}`}
                        checked={test.operator_review.final_pass === false}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            handleOverride(index, false);
                          } else if (test.operator_review.final_pass === false) {
                            handleOverride(index, null);
                          }
                        }}
                      />
                      <Label htmlFor={`fail-${test.test_id}`} className="text-sm">
                        Mark as Fail
                      </Label>
                    </div>
                  </div>
                  
                  <Textarea
                    placeholder="Add notes about this test result..."
                    value={test.operator_review.notes}
                    onChange={(e) => handleNotesChange(index, e.target.value)}
                    className="text-sm"
                    rows={2}
                  />
                  
                  {test.operator_review.reviewed_at && (
                    <p className="text-xs text-muted-foreground">
                      Reviewed: {new Date(test.operator_review.reviewed_at).toLocaleString()}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default QARunDetail;
